/**
 * Image Scanner - Scans CraftJS nodes to find all image references
 * Categorizes them as local (copy to assets/), external (keep URL), or base64 (inline)
 */

export interface ImageAsset {
  /** Original path/URL in the CraftJS props */
  originalUrl: string;
  /** Filename for the asset (e.g., "hero-bg.png") */
  filename: string;
  /** Import name for TSX (e.g., "heroBg") */
  importName: string;
  /** Whether to download and include in ZIP */
  isLocal: boolean;
}

interface CraftNode {
  type: { resolvedName: string };
  props: Record<string, unknown>;
  nodes?: string[];
  linkedNodes?: Record<string, string>;
}

interface CraftContent {
  [key: string]: CraftNode;
}

// Props that hold single image URLs
const SINGLE_IMAGE_KEYS = [
  "src",
  "logoUrl",
  "backgroundImage",
  "iconUrl",
  "beforeImage",
  "afterImage",
  "imageUrl",
  "favicon",
  "logo",
];

// Props that hold arrays of objects with image URLs
const ARRAY_IMAGE_KEYS: Record<string, string[]> = {
  slides: ["backgroundImage"],
  images: ["src"],
  extraIcons: ["iconUrl"],
  navItems: ["iconUrl"],
  extraButtons: ["iconUrl"],
  buttons: ["iconUrl"],
};

/**
 * Check if a URL is a local/relative path (should be downloaded and bundled)
 */
const isLocalUrl = (url: string): boolean => {
  if (!url || typeof url !== "string") return false;
  if (url.startsWith("data:")) return false;
  if (url.startsWith("http://") || url.startsWith("https://")) return false;
  return url.startsWith("/") || url.startsWith("static/");
};

/**
 * Check if a URL is external
 */
const isExternalUrl = (url: string): boolean => {
  if (!url || typeof url !== "string") return false;
  return url.startsWith("http://") || url.startsWith("https://");
};

/**
 * Check if a URL is base64
 */
const isBase64Url = (url: string): boolean => {
  if (!url || typeof url !== "string") return false;
  return url.startsWith("data:");
};

/**
 * Sanitize a string to create a valid JS identifier
 */
const toImportName = (filename: string): string => {
  const name = filename
    .replace(/\.[^.]+$/, "") // remove extension
    .replace(/[^a-zA-Z0-9]/g, "_") // non-alphanumeric to underscore
    .replace(/^(\d)/, "_$1") // prefix if starts with digit
    .replace(/_+/g, "_") // collapse underscores
    .replace(/^_|_$/g, ""); // trim underscores
  return name || "image";
};

/**
 * Get file extension from URL, default to png
 */
const getExtension = (url: string): string => {
  const match = url.match(/\.([a-zA-Z0-9]+)(?:\?.*)?$/);
  if (match) {
    const ext = match[1].toLowerCase();
    if (["png", "jpg", "jpeg", "svg", "webp", "gif", "ico"].includes(ext)) {
      return ext;
    }
  }
  return "png";
};

/**
 * Extract filename from URL
 */
const getFilename = (url: string): string => {
  const parts = url.split("/");
  const last = parts[parts.length - 1]?.split("?")[0] || "image";
  if (last.includes(".")) return last;
  return `${last}.png`;
};

/**
 * Scan all nodes in the CraftJS content and collect image assets
 */
export const scanImages = (content: CraftContent): ImageAsset[] => {
  const found = new Map<string, ImageAsset>();
  const usedFilenames = new Set<string>();

  const addImage = (url: string, context: string) => {
    if (!url || typeof url !== "string" || url.trim() === "") return;
    if (isBase64Url(url)) return; // base64 stays inline
    if (found.has(url)) return;

    const local = isLocalUrl(url);
    if (!local && !isExternalUrl(url)) return;

    // Only bundle local images
    if (!local) return;

    let filename = getFilename(url);
    // Deduplicate filenames
    if (usedFilenames.has(filename)) {
      const ext = getExtension(filename);
      const base = filename.replace(/\.[^.]+$/, "");
      let i = 2;
      while (usedFilenames.has(`${base}_${i}.${ext}`)) i++;
      filename = `${base}_${i}.${ext}`;
    }
    usedFilenames.add(filename);

    found.set(url, {
      originalUrl: url,
      filename,
      importName: toImportName(`${context}_${filename}`),
      isLocal: local,
    });
  };

  // Walk all nodes
  for (const [, node] of Object.entries(content)) {
    if (!node?.props) continue;
    const props = node.props;
    const typeName = node.type?.resolvedName || "unknown";

    // Check single image keys
    for (const key of SINGLE_IMAGE_KEYS) {
      if (props[key] && typeof props[key] === "string") {
        addImage(props[key] as string, `${typeName}_${key}`);
      }
    }

    // Check social links object (Footer)
    if (props.socialLinks && typeof props.socialLinks === "object") {
      // Social links use external URLs, skip
    }

    // Check array image keys
    for (const [arrayKey, imageFields] of Object.entries(ARRAY_IMAGE_KEYS)) {
      const arr = props[arrayKey];
      if (Array.isArray(arr)) {
        arr.forEach((item, index) => {
          if (item && typeof item === "object") {
            for (const field of imageFields) {
              const val = (item as Record<string, unknown>)[field];
              if (val && typeof val === "string") {
                addImage(val, `${typeName}_${arrayKey}_${index}_${field}`);
              }
            }
          }
        });
      }
    }

    // Check footer columns for any images
    if (props.columns && Array.isArray(props.columns)) {
      // Columns contain links, not images, skip
    }
  }

  return Array.from(found.values());
};

/**
 * Get the TSX reference for an image URL
 * - Local images: returns the import variable name
 * - External images: returns the URL string
 * - Base64: returns the data URI string
 */
export const getImageReference = (
  url: string,
  assets: ImageAsset[],
): { type: "import" | "url" | "base64"; value: string } => {
  if (!url || typeof url !== "string") {
    return { type: "url", value: "" };
  }

  if (isBase64Url(url)) {
    return { type: "base64", value: url };
  }

  const asset = assets.find((a) => a.originalUrl === url);
  if (asset) {
    return { type: "import", value: asset.importName };
  }

  return { type: "url", value: url };
};
